import { memo, useState, useMemo, useEffect, useRef } from 'react';
import TitleHeader from '../../components/MasterComponent/TitleHeader/TitleHeader';
import MultipleSelect, {
  SelectOption,
} from '../../components/MasterComponent/Select/MultipleSelect';
import * as echarts from 'echarts';
import './comparisonReports.css';
import { echartsColorPalette } from '../HealthMonitoring/config';
import { costFormatter2, handleError } from '../../utils/config';
import { CLOUD_PROVIDER_SERVICES, currencies } from '../../utils/constants';
import GraphLoader from '../../components/ServicesReports/GraphLoader';
import Chart from '../../components/MasterComponent/EchartsSetup/Echarts';
import CustomLegend from '../../components/MasterComponent/EchartsSetup/CustomLegend';
import ComparisonTable from './Table';
import { accountService } from '../../services';
import { useHistory } from 'react-router-dom';
import { format, subDays } from 'date-fns';
import { AWS_SERVICES_SHORTHAND, config } from './config';
import { useFetchData } from '../../hooks/useFetchData';
import DateRangePicker from '../CostAnalysis/DateRangePicker';
import { exportToFile } from '../../utils/formateXLSX';
import ExcelDownloadButton from '../../components/MasterComponent/Button/ExcelDownloadButton';
import CustomTooltip from '../../components/MasterComponent/CustomTooltip';
import { useSelector } from 'react-redux';
import { userState } from '../../store';

// interface ServiceData {
//   service: string;
//   [accountId: string]: string;
// }

interface AWSAccount {
  Id: number;
  Name: string;
  CloudProvider: string;
  Location: string;
  AccountId: string;
  AliasName: string;
  IsActive: number;
  // ... other properties
}

const ComparisonReport: React.FC = () => {
  const history = useHistory();
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [chartInstance, setChartInstance] = useState<echarts.ECharts | null>(
    null
  );

  // Set default date range (today - 6 days)
  const today = new Date();
  const defaultStartDate = subDays(today, 6);
  const defaultEndDate = today;

  // Date range state
  const [startDate, setStartDate] = useState<Date>(defaultStartDate);
  const [endDate, setEndDate] = useState<Date>(defaultEndDate);
  const [AWSAccountList, setAWSAccountList] = useState<AWSAccount[]>([]);
  const [isNetworkError, setNetworkError] = useState(false);
  const [servicesData, setServicesData] = useState<any[]>([]);
  const userData = useSelector(userState);
  const user = userData.userData;

  const { data: categoryOptions, loader: isFetchingOptions } = useFetchData({
    fetchData: () =>
      config.getCategoryOptions({ filterName: 'ProductServiceName' }),
    fetchOnMount: true,
  });

  const reqBodyRef = useRef({
    startDate: format(startDate, 'yyyy-MM-dd'),
    endDate: format(endDate, 'yyyy-MM-dd'),
    filters: {
      ProductServiceName: selectedServices,
    },
    accountId: selectedAccounts,
  });

  const { fetch, data, loader: isSubmitting } = useFetchData({
    fetchData: () => config.getMultiAccountsData(reqBodyRef.current),
    fetchOnMount: false,
  });

  const [isDownloading, setIsDownloading] = useState(false);
  const {
    fetch: fetchCostReport,
    data: fetchCostReportDownloadStatus,
    loader: isDownloadLoading,
  } = useFetchData({
    fetchData: () => config.downloadMultiAccountReport(reqBodyRef.current),
    fetchOnMount: false,
  });

  useEffect(() => {
    if (isDownloading && !isDownloadLoading && fetchCostReportDownloadStatus) {
      if (
        fetchCostReportDownloadStatus.fileBuffer &&
        fetchCostReportDownloadStatus.fileName
      ) {
        exportToFile(
          fetchCostReportDownloadStatus.fileBuffer,
          fetchCostReportDownloadStatus.fileName
        );
      }
      setIsDownloading(false);
    }
  }, [isDownloading, isDownloadLoading, fetchCostReportDownloadStatus]);

  const downloadCsvHandler = () => {
    setIsDownloading(true);
    fetchCostReport();
  };

  useEffect(() => {
    reqBodyRef.current = {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
      filters: {
        ProductServiceName: selectedServices,
      },
      accountId: selectedAccounts,
    };
  }, [startDate, endDate, selectedServices, selectedAccounts]);

  let isCanceled = false;

  useEffect(() => {
    if (data && !isSubmitting && !isCanceled) {
      setServicesData(data);
    }

    return () => {
      isCanceled = true;
    };
  }, [data, isSubmitting]);

  const AWSAccountListHandler = async () => {
    try {
      const result = await accountService.getAccountDetails(
        CLOUD_PROVIDER_SERVICES.AWS
      );
      setAWSAccountList(result || []);
    } catch (e: any) {
      if (e === 'Network Error') {
        setNetworkError(true);
      }
      if (e && e.error && e.error.message) {
        handleError(e, history);
      }
    }
  };

  useEffect(() => {
    AWSAccountListHandler();
  }, []);

  // Fetch data when accounts or services change
  useEffect(() => {
    if (AWSAccountList.length > 0) {
      fetch();
    }
  }, [selectedAccounts, selectedServices, AWSAccountList]);

  if (isNetworkError) {
    throw new Error('Network Error');
  }

  // Handle date range change
  const handleDateRangeChange = (start: Date, end: Date) => {
    setStartDate(start);
    setEndDate(end);
    setTimeout(() => {
      fetch();
    }, 0);
  };

  // Generate account options from API data
  const accountOptions: SelectOption[] = useMemo(() => {
    if (!AWSAccountList || !Array.isArray(AWSAccountList)) return [];

    // Filter only accounts that exist in user.UserAccounts
    const userAccountIds =
      user?.UserAccounts?.map((ua: any) => ua.accountId) || [];

    return AWSAccountList.filter(
      (account) => account.IsActive === 1 && userAccountIds.includes(account.Id)
    ).map((account, index) => ({
      id: account.Id || index,
      label: `${account.AliasName} (${account.AccountId})`,
      value: account.AccountId.toString(),
    }));
  }, [AWSAccountList, user?.UserAccounts]);

  // Generate service options from API data
  const serviceOptions: SelectOption[] = useMemo(() => {
    if (!categoryOptions || !Array.isArray(categoryOptions)) return [];

    return categoryOptions
      .map((service, index) => ({
        id: index + 1,
        label: service,
        value: service,
      }))
      .filter((item) => item.value);
  }, [categoryOptions]);

  // Handle chart initialization
  const handleChartInit = (chart: echarts.ECharts | null) => {
    setChartInstance(chart);
  };

  // Generate colors for chart
  const generateColors = (count: number) => echartsColorPalette.slice(0, count);

  // Process data for chart - Group by accounts, show services on x-axis
  const processedData = useMemo(() => {
    if (!servicesData || !Array.isArray(servicesData)) return [];

    // Get all available account IDs from the data
    const availableAccountIds = Array.from(
      new Set(
        servicesData.flatMap((item) =>
          Object.keys(item).filter((key) => key !== 'service')
        )
      )
    );

    // Create series for each account/region
    return availableAccountIds.map((accountId) => ({
      accountId,
      data: servicesData.map((item) => parseFloat(item[accountId] || '0')),
    }));
  }, [servicesData]);

  const colors = generateColors(processedData.length);

  // Get region label for legend
  // const getRegionLabel = (accountId: string) => {
  //   const option = accountOptions.find((opt) => opt.value === accountId);
  //   return option ? option.label : `Account ${accountId}`;
  // };

  // Get current services for chart x-axis
  const currentServices = useMemo(() => {
    if (!servicesData || !Array.isArray(servicesData)) return [];

    return servicesData.map((item) => item.service);
  }, [servicesData]);

  const getServiceShorthand = (serviceName: string): string => {
    return AWS_SERVICES_SHORTHAND[serviceName] || serviceName;
  };

  // Chart options
  const getChartOptions = (): echarts.EChartsOption => {
    const series = processedData.map((item, index) => ({
      // name: getRegionLabel(item.accountId),
      name: item.accountId,
      type: 'bar' as const,
      data: item.data,
      itemStyle: {
        color: colors[index],
      },
      barMaxWidth: 60,
    }));

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
        },
        confine: false,
        enterable: true,
        formatter: (params: any) => {
          const header = `<div class="echarts-tooltip-title">${params[0].axisValue}</div>`;

          const items = params
            .map((param: any) => {
              const colorSpan = `<span class="echarts-tooltip-color" style="background-color: ${param.color}"></span>`;
              return `${colorSpan} <span class="echarts-tooltip-name">${
                param.seriesName
              } :</span> <span class="echarts-tooltip-value">${costFormatter2(
                currencies.USD.shortHand,
                param.value
              )}</span>`;
            })
            .join('<br/>');

          return `<div class="echarts-tooltip">${header}${items}</div>`;
        },
      },
      legend: {
        show: false,
      },
      grid: {
        left: '1%',
        right: '2%',
        bottom: '2%',
        top: '5%',
        containLabel: true,
      },
      dataZoom: [
        {
          type: 'inside',
          show: true,
        },
      ],
      xAxis: {
        type: 'category',
        data: currentServices,
        axisLabel: {
          rotate: 45,
          interval: 0,
          fontSize: 11,
          color: '#666',
          formatter: (value: string) => {
            // Use shorthand mapping
            const shorthand = getServiceShorthand(value);

            // If shorthand is still too long, truncate it
            if (shorthand.length > 15) {
              return shorthand.substring(0, 15) + '...';
            }
            return shorthand;
          },
          margin: 15,
        },
        axisLine: {
          lineStyle: {
            color: '#e5e7eb',
          },
        },
        axisTick: {
          alignWithLabel: true,
          lineStyle: {
            color: '#e5e7eb',
          },
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: '${value}',
          color: '#666',
          fontSize: 11,
        },
        nameTextStyle: {
          color: '#666',
          fontSize: 12,
        },
      },
      series: series,
    };
  };

  const renderChart = () => {
    if (isSubmitting || isFetchingOptions) {
      return (
        <div className="chartLoaderContainer">
          <GraphLoader
            style={{
              height: '41vh',
              width: '91%',
              margin: '5rem auto',
            }}
            isLoading={true}
          />
        </div>
      );
    }

    if (processedData.length > 0 && servicesData.length > 0) {
      return (
        <div className="chartContainer">
          <div className="chartWrapper">
            <Chart
              option={getChartOptions()}
              style={{ height: '100%', width: '100%' }}
              onChartInit={handleChartInit}
            />
          </div>
          <CustomLegend
            legendData={processedData.map(
              (item) =>
                // getRegionLabel(item.accountId)
                item.accountId
            )}
            colors={colors}
            chartInstance={chartInstance}
          />
        </div>
      );
    }

    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        No data available for the selected filters
      </div>
    );
  };

  return (
    <div className="app-container">
      <TitleHeader>Comparison Reports</TitleHeader>

      <div className="comparisonReportContent">
        <div className="filtersSection">
          <CustomTooltip
            title={
              isDownloadLoading
                ? 'Download in progress...'
                : 'Export current view as CSV'
            }
          >
            <ExcelDownloadButton
              isExcelDownloading={isDownloadLoading}
              onDownloadCsv={downloadCsvHandler}
            />
          </CustomTooltip>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateRangeChange}
            showPresetRanges={true}
            defaultStartDate={defaultStartDate}
            defaultEndDate={defaultEndDate}
          />
          <div className="filterGroup">
            <MultipleSelect
              options={accountOptions}
              selectedValues={selectedAccounts}
              onChange={setSelectedAccounts}
              label="Select Accounts"
              placeholder="Select accounts..."
              maxVisibleChips={2}
              className="accountSelect"
            />
          </div>

          <div className="filterGroup">
            <MultipleSelect
              options={serviceOptions}
              selectedValues={selectedServices}
              onChange={setSelectedServices}
              label="Select Services"
              placeholder="Select services..."
              maxVisibleChips={2}
              className="serviceSelect"
            />
          </div>
        </div>

        {renderChart()}

        {/* Add the comparison table */}
        <ComparisonTable
          data={servicesData}
          selectedAccounts={processedData.map((item) => item.accountId)}
          // accountOptions={accountOptions}
          currency="USD"
          isLoading={isSubmitting}
        />
      </div>
    </div>
  );
};

export default memo(ComparisonReport);
